import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  BOARD_VISIBILITIES,
  SHORT_LINK_MAX_LENGTH,
  boardRoleSchema,
} from './board';
import {
  apiTokenExpiryPresetSchema,
  apiTokenIssuerSchema,
  connectedAppBearerTokenSchema,
  connectedAppGrantStatusSchema,
  connectedAppScopeSchema,
} from './connected-app';
import { enterpriseRoleSchema } from './enterprise';
import { ApiError } from './errors';
import { ulidSchema } from './id';
import {
  EMAIL_MAX_LENGTH,
  INITIALS_MAX_LENGTH,
  displayNameSchema,
  emailSchema,
  initialsOverrideSchema,
  publicProfileSchema,
} from './user';
import {
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_SHORT_NAME_MAX_LENGTH,
  workspaceRoleSchema,
} from './workspace';

/**
 * SA-8 — the platform admin console's wire contracts.
 *
 * `docs/notes/superadmin-requirements.md` SA-8 and SA-11, build plan §7.4. Every
 * route these shapes serialise lives in `apps/server/src/routes/admin.ts`, and
 * every one of them answers a non-superadmin with the ordinary unmatched-route
 * 404, so nothing here is ever seen by anyone but a platform superadmin.
 *
 * ---------------------------------------------------------------------------
 * ⚠ ADMIN-ONLY COPIES, NEVER WIDENINGS
 * ---------------------------------------------------------------------------
 * `adminUserSummarySchema` extends a copy of `publicProfileSchema` with the
 * account's email, flag and lifecycle stamps. None of those may reach
 * `publicProfileSchema` itself, which serialises other people's profiles on
 * every member list, comment and activity entry in the product.
 *
 * ---------------------------------------------------------------------------
 * CONVENTIONS
 * ---------------------------------------------------------------------------
 *   - Responses are `z.object`; querystrings and bodies are `z.strictObject`, so
 *     an unknown key from a superadmin is a 422 rather than a silently ignored
 *     filter. (From anyone else it is the 404: the guard runs before validation.)
 *   - Types come from `z.infer` only.
 *   - Timestamps are the driver's strings, `z.string().min(1)`.
 *   - Paging is keyset on `id DESC` alone. ULIDs are monotonic, so newest-first
 *     by id is newest-first by creation, and the primary key serves the backward
 *     scan. The server fetches `limit + 1` rows; `nextCursor` is null exactly when
 *     there was no extra row.
 */

export const ADMIN_PAGE_SIZE_DEFAULT = 50;
export const ADMIN_PAGE_SIZE_MAX = 100;
/** The longest search term: an email address is the longest thing worth typing. */
export const ADMIN_SEARCH_QUERY_MAX_LENGTH = EMAIL_MAX_LENGTH;
/** Each membership list of `GET /admin/users/:id` stops here; `counts` carries the totals. */
export const ADMIN_USER_MEMBERSHIPS_MAX = 500;
/** `superadmin_audit_entries.reason`, `varchar(1000)`. */
export const ADMIN_REASON_MAX_LENGTH = 1000;

/**
 * The dotted names `ck_superadmin_audit_entries_action` admits — one list, here,
 * which the server's writer (`apps/server/src/admin/audit.ts`) imports.
 * `superadmin-admin-console.integration.test.ts` holds it against the live
 * constraint definition, so a name added to one and not the other reds.
 */
export const SUPERADMIN_AUDIT_ACTIONS = [
  'super.granted',
  'super.revoked',
  'super.board_opened',
  'super.mutation',
  'super.user_deactivated',
  'super.user_reactivated',
  'super.sessions_revoked',
  'super.bearer_refused',
  // B85 D11 (`0065_api_token_audit_actions.sql`): a superadmin issuing or
  // revoking a member's API token. Appended, never inserted: the web
  // catalogue's `admin.audit.action.*` keys are pinned in this order.
  'super.api_token_issued',
  'super.api_token_revoked',
  // `docs/notes/admin-user-creation.md` UC-D6
  // (`0066_superadmin_user_created_audit_action.sql`): a superadmin creating an
  // account through `POST /admin/users`. Appended, like the two above.
  'super.user_created',
  // Member initials (owner-directed, `0073_member_initials.sql`): a superadmin
  // setting or clearing an account's initials through
  // `PATCH /admin/users/:id/initials`. Appended, like the three above.
  'super.user_initials_set',
] as const;

export const superadminAuditActionSchema = z.enum(SUPERADMIN_AUDIT_ACTIONS);
export type SuperadminAuditAction = z.infer<typeof superadminAuditActionSchema>;

// ---------------------------------------------------------------------------
// Cursors
// ---------------------------------------------------------------------------

/** The four paged lists. A cursor names the one it was minted for. */
export const ADMIN_STREAMS = ['users', 'workspaces', 'boards', 'audit'] as const;
export type AdminStream = (typeof ADMIN_STREAMS)[number];

export const adminCursorSchema = z.string().min(1).max(128);

const adminCursorPayloadSchema = z.strictObject({
  stream: z.enum(ADMIN_STREAMS),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded — a fifth module-private copy.
 *
 * ⚠ Deliberately not imported from `./activity` or its siblings, whose own
 * header says the same: `Buffer` is Node-only and this package is bundled into
 * `apps/web`, and `btoa`/`atob` are not declared under this workspace's
 * `lib: ["ES2023"]`. Each copy is private so that no module's cursor format
 * becomes another's dependency.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** ASCII in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/** Unpadded base64url in, latin-1 out; throws on a foreign character or an impossible length. */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/** The cursor that fetches the page after the row `id` on `stream`. */
export function encodeAdminCursor(stream: AdminStream, id: string): string {
  return toBase64Url(JSON.stringify(adminCursorPayloadSchema.parse({ stream, id })));
}

/**
 * The id a cursor resumes after, **only if it was minted for `stream`**.
 *
 * ⚠ A malformed cursor, or one from another list, is a 422 and never "start from
 * the top" — the argument `./activity` gives: a silent restart turns "Load more"
 * into an endless loop over page one with a 200 every time. The stream check is
 * what stops a users cursor, which is a perfectly good ULID, from paging the
 * boards list from an arbitrary point.
 */
export function decodeAdminCursor(cursor: string, stream: AdminStream): string {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this list'],
    });
  };
  let payload: unknown;
  try {
    payload = JSON.parse(fromBase64Url(cursor));
  } catch {
    return invalid();
  }
  const parsed = adminCursorPayloadSchema.safeParse(payload);
  if (!parsed.success || parsed.data.stream !== stream) return invalid();
  return parsed.data.id;
}

// ---------------------------------------------------------------------------
// Querystrings and bodies
// ---------------------------------------------------------------------------

const adminLimitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(ADMIN_PAGE_SIZE_MAX)
  .default(ADMIN_PAGE_SIZE_DEFAULT);

const adminSearchSchema = z.string().trim().min(1).max(ADMIN_SEARCH_QUERY_MAX_LENGTH);

/** `GET /admin/users` — `q` matches email, username or display name, or is an exact id. */
export const adminUserListQuerySchema = z.strictObject({
  q: adminSearchSchema.optional(),
  limit: adminLimitSchema,
  cursor: adminCursorSchema.optional(),
});
export type AdminUserListQuery = z.infer<typeof adminUserListQuerySchema>;

/** `GET /admin/workspaces` — `q` matches name or short name, or is an exact id. */
export const adminWorkspaceListQuerySchema = z.strictObject({
  q: adminSearchSchema.optional(),
  limit: adminLimitSchema,
  cursor: adminCursorSchema.optional(),
});
export type AdminWorkspaceListQuery = z.infer<typeof adminWorkspaceListQuerySchema>;

/**
 * `GET /admin/boards`. `closed` is a `z.stringbool` with its sets pinned to the
 * two literals — `./notification`'s rule — so `?closed=yes` is a 422 rather than
 * a filter that quietly means something. Like that one it is case-insensitive
 * (`?closed=TRUE` is `true`), which cannot mean the wrong thing.
 */
export const adminBoardListQuerySchema = z.strictObject({
  q: adminSearchSchema.optional(),
  visibility: z.enum(BOARD_VISIBILITIES).optional(),
  closed: z.stringbool({ truthy: ['true'], falsy: ['false'] }).optional(),
  limit: adminLimitSchema,
  cursor: adminCursorSchema.optional(),
});
export type AdminBoardListQuery = z.infer<typeof adminBoardListQuerySchema>;

/** `GET /admin/audit`. `target` matches any of the four `target_*` columns. */
export const adminAuditQuerySchema = z.strictObject({
  actor: ulidSchema.optional(),
  target: ulidSchema.optional(),
  action: superadminAuditActionSchema.optional(),
  limit: adminLimitSchema,
  cursor: adminCursorSchema.optional(),
});
export type AdminAuditQuery = z.infer<typeof adminAuditQuerySchema>;

/**
 * The body of the three account actions. `confirm: true` is the Enterprise
 * lifecycle's guard against a stray POST; `reason` is required because the
 * audit row is the only record of why an account was closed.
 */
export const adminUserLifecycleBodySchema = z.strictObject({
  confirm: z.literal(true),
  reason: z.string().trim().min(1).max(ADMIN_REASON_MAX_LENGTH),
});
export type AdminUserLifecycleBody = z.infer<typeof adminUserLifecycleBodySchema>;

/**
 * `PATCH /admin/users/:id/initials` — a superadmin sets (`initials: "AB"`) or
 * clears (`initials: null`, back to the display-name derivation) the initials
 * an account's avatar circle shows everywhere (owner-directed; migration
 * `0073_member_initials.sql`). Field-scoped (SC-4): this body carries one
 * field and nothing else, strict, so `displayName`, `initialsColour` and every
 * other key is a 422.
 *
 * `initials` is REQUIRED and nullable: an absent key is a 422 rather than a
 * silent clear. `reason` mirrors the lifecycle body's bounds but is optional —
 * initials are cosmetic, and the audit row records the act with or without
 * one. No `confirm`: the lifecycle body's stray-POST guard protects actions
 * that close an account, and this one changes two letters.
 */
export const adminUserInitialsBodySchema = z.strictObject({
  initials: initialsOverrideSchema.nullable(),
  reason: z.string().trim().min(1).max(ADMIN_REASON_MAX_LENGTH).optional(),
});
export type AdminUserInitialsBody = z.infer<typeof adminUserInitialsBodySchema>;

/** `connected_apps.tool_name`, `varchar(255)` — the name members read in activity. */
export const ADMIN_API_TOKEN_NAME_MAX_LENGTH = 255;

/**
 * `POST /admin/users/:userId/api-tokens` — AT-3 of
 * `docs/notes/api-token-requirements.md` (block B85).
 *
 * The superadmin chooses a name, a scope and an expiry PRESET, and nothing else.
 * Strict, so `expiresAt` (a date is computed from the server's clock, D5),
 * `userId` (the path names the owner), `issuedBy` (the session names the
 * issuer) and `token` (none exists yet, D4) are each a 422.
 *
 * ⚠ `name`, not `toolName`: the brief's spelling. The column and the member
 * copy say `tool_name`/`toolName`; the server maps between them in one place.
 * `scope` has no default on the wire — the console's form defaults to read
 * only (D6), and the request states what it chose.
 */
export const adminApiTokenIssueBodySchema = z.strictObject({
  name: z.string().trim().min(1).max(ADMIN_API_TOKEN_NAME_MAX_LENGTH),
  scope: connectedAppScopeSchema,
  expiresIn: apiTokenExpiryPresetSchema,
});
export type AdminApiTokenIssueBody = z.infer<typeof adminApiTokenIssueBodySchema>;

/**
 * The password bounds of a superadmin-created account — Better Auth's own
 * `minPasswordLength` and `maxPasswordLength` defaults, which `better-auth.ts`
 * does not override, so a password the console accepts is one sign-up would
 * have accepted too. `apps/server`'s `user-creation.test.ts` holds both
 * against the constructed instance's context.
 */
export const ADMIN_USER_PASSWORD_MIN_LENGTH = 8;
export const ADMIN_USER_PASSWORD_MAX_LENGTH = 128;

/**
 * `POST /admin/users` — UC-2 of `docs/notes/admin-user-creation.md`.
 *
 * `email` and `displayName` are the sign-up boundary's own schemas
 * (`emailSchema`, and `displayNameSchema`, which is what the Better Auth create
 * hook validates a name with — trimmed, 1 to 128), so the console cannot store
 * a row sign-up would have refused. `emailSchema` deliberately does not
 * lower-case; the server does, as Better Auth does.
 *
 * `password` is optional (UC-D3): absent, the server generates one and returns
 * it once. Strict, so `username`, `isSuper`, `emailVerified` and every other
 * column a superadmin might hope to set is a 422 rather than a silently ignored
 * key.
 */
export const adminUserCreateBodySchema = z.strictObject({
  email: emailSchema,
  displayName: displayNameSchema,
  password: z
    .string()
    .min(ADMIN_USER_PASSWORD_MIN_LENGTH)
    .max(ADMIN_USER_PASSWORD_MAX_LENGTH)
    .optional(),
});
export type AdminUserCreateBody = z.infer<typeof adminUserCreateBodySchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** One account on `GET /admin/users`. Deactivated and soft-deleted accounts are listed. */
export const adminUserSummarySchema = publicProfileSchema.extend({
  email: z.string().max(EMAIL_MAX_LENGTH),
  isSuper: z.boolean(),
  createdAt: z.string().min(1),
  deactivatedAt: z.string().min(1).nullable(),
  deletedAt: z.string().min(1).nullable(),
  /** Live workspaces the account is a member of. */
  workspaceCount: z.number().int().min(0),
});
export type AdminUserSummary = z.infer<typeof adminUserSummarySchema>;

export const adminUserPageSchema = z.object({
  items: z.array(adminUserSummarySchema),
  nextCursor: adminCursorSchema.nullable(),
});
export type AdminUserPage = z.infer<typeof adminUserPageSchema>;

export const adminUserWorkspaceMembershipSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
  role: workspaceRoleSchema,
});

/** A board title, never its content — SA-8's line. */
export const adminUserBoardMembershipSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().min(1).max(SHORT_LINK_MAX_LENGTH),
  workspaceId: ulidSchema,
  role: boardRoleSchema,
  closed: z.boolean(),
});

export const adminUserEnterpriseMembershipSchema = z.object({
  id: ulidSchema,
  name: z.string().max(255),
  role: enterpriseRoleSchema,
});

/**
 * `GET /admin/users/:id`. Each list holds at most `ADMIN_USER_MEMBERSHIPS_MAX`
 * rows, newest first; `counts` are the totals, so a truncated list is visible as
 * a count larger than its length rather than as a quietly short answer.
 * Memberships of soft-deleted workspaces and boards are not listed.
 */
export const adminUserDetailSchema = adminUserSummarySchema.extend({
  memberships: z.object({
    workspaces: z.array(adminUserWorkspaceMembershipSchema),
    boards: z.array(adminUserBoardMembershipSchema),
    enterprises: z.array(adminUserEnterpriseMembershipSchema),
    counts: z.object({
      workspaces: z.number().int().min(0),
      boards: z.number().int().min(0),
      enterprises: z.number().int().min(0),
    }),
  }),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

/**
 * `GET /admin/users/:id` and `PATCH /admin/users/:id/initials` (member
 * initials, `0073_member_initials.sql`) — the detail above plus one field.
 *
 * A COPY, NOT A WIDENING (CLAUDE.md: "Extend a shipped schema on a copy, never
 * widen it in place"). `adminUserDetailSchema` also serialises
 * `POST /admin/users`'s 201 (`adminCreatedUserSchema.user`), a route outside
 * this feature, and that answer must not grow the field.
 *
 * `initialsOverride` is the stored `users.initials` override, or `null` when the
 * account's `initials` are derived from its display name. The console needs
 * both: `initials` (inherited) is what every avatar draws, and this says whether
 * that value was pinned by a superadmin — "AB" derived and "AB" pinned look
 * identical otherwise. Admin-only: `publicProfileSchema` never carries it.
 */
export const adminUserDetailWithInitialsSchema = adminUserDetailSchema.extend({
  initialsOverride: z.string().min(1).max(INITIALS_MAX_LENGTH).nullable(),
});
export type AdminUserDetailWithInitials = z.infer<
  typeof adminUserDetailWithInitialsSchema
>;

/**
 * The 201 of `POST /admin/users` — UC-2 of `docs/notes/admin-user-creation.md`.
 *
 * `user` is exactly what `GET /admin/users/:id` answers for the new account, so
 * the console can show the detail page without a second read.
 * `generatedPassword` is the server-generated password, returned **once** and
 * served `Cache-Control: no-store`; `null` when the superadmin supplied the
 * password themselves (UC-D3), because echoing a typed secret back buys nothing.
 */
export const adminCreatedUserSchema = z.object({
  user: adminUserDetailSchema,
  generatedPassword: z.string().min(ADMIN_USER_PASSWORD_MIN_LENGTH).nullable(),
});
export type AdminCreatedUser = z.infer<typeof adminCreatedUserSchema>;

/** `POST /admin/users/:id/sessions/revoke`. Zero is a legitimate answer. */
export const adminSessionsRevokeResultSchema = z.object({
  sessionsRevoked: z.number().int().min(0),
});
export type AdminSessionsRevokeResult = z.infer<typeof adminSessionsRevokeResultSchema>;

/** One live workspace on `GET /admin/workspaces`. */
export const adminWorkspaceSummarySchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
  enterpriseId: ulidSchema.nullable(),
  memberCount: z.number().int().min(0),
  /** Live boards. */
  boardCount: z.number().int().min(0),
  createdAt: z.string().min(1),
});
export type AdminWorkspaceSummary = z.infer<typeof adminWorkspaceSummarySchema>;

export const adminWorkspacePageSchema = z.object({
  items: z.array(adminWorkspaceSummarySchema),
  nextCursor: adminCursorSchema.nullable(),
});
export type AdminWorkspacePage = z.infer<typeof adminWorkspacePageSchema>;

/**
 * One live board on `GET /admin/boards`. **Titles only, never card content**
 * (SA-8). `shortLink` is required: the console links each row to the board face.
 */
export const adminBoardSummarySchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().min(1).max(SHORT_LINK_MAX_LENGTH),
  workspaceId: ulidSchema,
  workspaceName: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  visibility: z.enum(BOARD_VISIBILITIES),
  closed: z.boolean(),
  memberCount: z.number().int().min(0),
  /** The newest activity entry on the board, or null for a board nobody has touched. */
  lastActivityAt: z.string().min(1).nullable(),
});
export type AdminBoardSummary = z.infer<typeof adminBoardSummarySchema>;

export const adminBoardPageSchema = z.object({
  items: z.array(adminBoardSummarySchema),
  nextCursor: adminCursorSchema.nullable(),
});
export type AdminBoardPage = z.infer<typeof adminBoardPageSchema>;

/** Who performed an audited act, when it was a signed-in account. */
export const adminAuditActorSchema = z.object({
  id: ulidSchema,
  username: z.string(),
  displayName: z.string(),
});

/** One `superadmin_audit_entries` row (SA-11). Append-only: no route edits one. */
export const superadminAuditEntrySchema = z.object({
  id: ulidSchema,
  action: superadminAuditActionSchema,
  actor: adminAuditActorSchema.nullable(),
  /** The CLI operator, for `super.granted` / `super.revoked`. */
  operator: z.string().nullable(),
  targetUserId: ulidSchema.nullable(),
  targetBoardId: ulidSchema.nullable(),
  targetWorkspaceId: ulidSchema.nullable(),
  targetEnterpriseId: ulidSchema.nullable(),
  reason: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()).nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string().min(1),
});
export type SuperadminAuditEntryView = z.infer<typeof superadminAuditEntrySchema>;

export const adminAuditPageSchema = z.object({
  items: z.array(superadminAuditEntrySchema),
  nextCursor: adminCursorSchema.nullable(),
});
export type AdminAuditPage = z.infer<typeof adminAuditPageSchema>;

/**
 * One API token as the admin console sees it — AT-5, and the 200 of
 * `DELETE /admin/api-tokens/:id`. AT-3's 201 is `adminApiTokenIssuedSchema`, a
 * copy of this with the token added.
 *
 * A NEW schema, not a widening of `connectedAppGrantSchema` (which serialises
 * the member's POST and DELETE responses) nor of the member copy. The fields
 * are the member copy's, with `toolName` spelled `name`; `status`, `tokenHint`
 * and `issuedBy` come from the one derivation both copies share
 * (`apps/server/src/external/api-token-issuance.ts`).
 *
 * ⚠ **NO KEY CARRIES A TOKEN.** The list and the revoke never return the
 * credential, and neither does anything a superadmin can re-read. `tokenHint`
 * is the last four characters of the live token (`varchar(8)`), which is not a
 * secret and never parses as a bearer token. B85 D4 kept the token from the
 * superadmin entirely; the owner reversed that on 2026-09-14
 * (`docs/notes/admin-user-creation.md` UC-D8), and the ONE response that now
 * carries it is issuance's, on the copy below.
 */
export const adminApiTokenSchema = z.object({
  id: ulidSchema,
  name: z.string().min(1).max(ADMIN_API_TOKEN_NAME_MAX_LENGTH),
  scope: connectedAppScopeSchema,
  status: connectedAppGrantStatusSchema,
  grantedAt: z.string().min(1),
  expiresAt: z.string().min(1).nullable(),
  lastActiveAt: z.string().min(1).nullable(),
  revokedAt: z.string().min(1).nullable(),
  tokenHint: z.string().max(8).nullable(),
  /** `null` when the member issued the token to themselves. */
  issuedBy: apiTokenIssuerSchema.nullable(),
});
export type AdminApiToken = z.infer<typeof adminApiTokenSchema>;

/**
 * `POST /admin/users/:userId/api-tokens` (201) — UC-3 of
 * `docs/notes/admin-user-creation.md` (UC-D8, reversing B85 D4): the admin row
 * plus the cleartext `token`, minted at issuance and returned exactly once, so
 * the superadmin can hand it over with the account. A COPY, never a widening:
 * `adminApiTokenSchema` still serialises the list and the revoke, which must
 * not carry it. Served `Cache-Control: no-store`.
 */
export const adminApiTokenIssuedSchema = adminApiTokenSchema.extend({
  token: connectedAppBearerTokenSchema,
});
export type AdminApiTokenIssued = z.infer<typeof adminApiTokenIssuedSchema>;

/** `GET /admin/users/:userId/api-tokens` — every grant the account owns, newest first. */
export const adminApiTokenListSchema = z.object({
  apiTokens: z.array(adminApiTokenSchema),
});
export type AdminApiTokenList = z.infer<typeof adminApiTokenListSchema>;
